import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Stethoscope,
  XCircle,
} from "lucide-react";

const ReferralDecisionPage = () => {
  const { id } = useParams();

  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [decisionType, setDecisionType] = useState(null);
  const [done, setDone] = useState(null); // accept | decline
  const [error, setError] = useState("");

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const referralStatusLabel = useMemo(() => {
    if (done === "accept") return "Referral accepted";
    if (done === "decline") return "Referral declined";
    return "Awaiting decision";
  }, [done]);

  const fetchReferral = async () => {
    try {
      setLoading(true);
      setError("");

      if (!id) {
        setError("Referral ID is missing from this link.");
        setReferral(null);
        return;
      }

      if (!backendUrl) {
        setError(
          "Referral service is not configured correctly. Please contact Zaviramed support."
        );
        setReferral(null);
        return;
      }

      const res = await axios.get(`${backendUrl}/referrals/${id}`, {
        timeout: 30000,
      });

      if (!res.data?.referral) {
        setError("Referral not found or no longer available.");
        setReferral(null);
        return;
      }

      setReferral(res.data.referral);
    } catch (err) {
      console.error("Referral fetch error:", err);

      if (err.code === "ECONNABORTED") {
        setError(
          "The request took too long. Please check your internet connection and try again."
        );
        return;
      }

      setError(
        err.response?.data?.message ||
          "We could not load this referral. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferral();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDecision = async (type) => {
    try {
      setDecisionLoading(true);
      setDecisionType(type);
      setError("");

      if (!id) {
        setError("Referral ID is missing from this link.");
        return;
      }

      if (!backendUrl) {
        setError(
          "Referral service is not configured correctly. Please contact Zaviramed support."
        );
        return;
      }

      await axios.put(`${backendUrl}/referrals/${id}/${type}`, null, {
        timeout: 30000,
      });

      setDone(type);
    } catch (err) {
      console.error("Referral decision error:", err);

      if (err.code === "ECONNABORTED") {
        setError(
          "The request took too long. Please check your internet connection and try again."
        );
        return;
      }

      setError(
        err.response?.data?.message ||
          "We could not submit your decision. Please try again."
      );
    } finally {
      setDecisionLoading(false);
      setDecisionType(null);
    }
  };

  const patientName = referral?.patientId?.name || "Unknown patient";
  const patientEmail = referral?.patientId?.email || "";
  const patientPhone =
    referral?.patientId?.phoneNumber ||
    referral?.patientId?.phone ||
    referral?.patientId?.whatsapp ||
    "";

  if (loading) {
    return (
      <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
        <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
            <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
              <div>
                <img
                  src="/logo_new_nobg.png"
                  alt="Zaviramed"
                  className="h-12 w-auto object-contain"
                />

                <div className="mt-16">
                  <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                    <ShieldCheck size={15} />
                    Secure referral review
                  </div>

                  <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                    Reviewing patient referral details.
                  </h1>

                  <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                    Please wait while we retrieve the referral summary and
                    clinical information.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 text-sm text-slate-300">
                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Service</span>
                  <span className="font-medium text-white">Referral</span>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Provider</span>
                  <span className="font-medium text-white">Zaviramed</span>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Status</span>
                  <span className="font-medium text-white">Loading</span>
                </div>
              </div>
            </aside>

            <section className="p-5 sm:p-8 md:p-10 lg:p-12">
              <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
                <img
                  src="/logo_new_nobg.png"
                  alt="Zaviramed"
                  className="h-10 w-auto object-contain"
                />

                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <ShieldCheck size={14} />
                  Secure
                </div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                <Loader2 size={14} className="animate-spin" />
                Loading referral
              </div>

              <div className="mt-8 max-w-2xl">
                <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700">
                  <Loader2 size={32} className="animate-spin" />
                </div>

                <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                  Loading referral details
                </h2>

                <p className="mt-4 text-base leading-7 text-slate-600">
                  We are retrieving the patient referral details. This should
                  only take a moment.
                </p>
              </div>
            </section>
          </div>
        </section>
      </main>
    );
  }

  if (!referral) {
    return (
      <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
        <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
            <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
              <div>
                <img
                  src="/logo_new_nobg.png"
                  alt="Zaviramed"
                  className="h-12 w-auto object-contain"
                />

                <div className="mt-16">
                  <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-red-300">
                    <AlertCircle size={15} />
                    Referral unavailable
                  </div>

                  <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                    We could not load this referral.
                  </h1>

                  <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                    The referral may have expired, been removed, or the link may
                    be invalid.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 text-sm text-slate-300">
                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Service</span>
                  <span className="font-medium text-white">Referral</span>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Referral ID</span>
                  <span className="max-w-[180px] truncate font-medium text-white">
                    {id || "Not available"}
                  </span>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Status</span>
                  <span className="font-medium text-red-300">Unavailable</span>
                </div>
              </div>
            </aside>

            <section className="p-5 sm:p-8 md:p-10 lg:p-12">
              <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
                <img
                  src="/logo_new_nobg.png"
                  alt="Zaviramed"
                  className="h-10 w-auto object-contain"
                />

                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <ShieldCheck size={14} />
                  Secure
                </div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">
                <AlertCircle size={14} />
                Referral not found
              </div>

              <div className="mt-8 max-w-2xl">
                <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-2xl border border-red-200 bg-red-50 text-red-700">
                  <XCircle size={32} />
                </div>

                <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                  Referral unavailable
                </h2>

                <p className="mt-4 text-base leading-7 text-slate-600">
                  {error || "This referral could not be found."}
                </p>

                <button
                  type="button"
                  onClick={fetchReferral}
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-4 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.99] sm:w-auto"
                >
                  <RefreshCw size={18} />
                  Retry
                </button>
              </div>
            </section>
          </div>
        </section>
      </main>
    );
  }

  if (done) {
    const accepted = done === "accept";

    return (
      <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
        <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
            <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
              <div>
                <img
                  src="/logo_new_nobg.png"
                  alt="Zaviramed"
                  className="h-12 w-auto object-contain"
                />

                <div className="mt-16">
                  <div
                    className={`mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold ${
                      accepted ? "text-emerald-300" : "text-red-300"
                    }`}
                  >
                    {accepted ? (
                      <CheckCircle2 size={15} />
                    ) : (
                      <XCircle size={15} />
                    )}
                    Decision submitted
                  </div>

                  <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                    {accepted
                      ? "Referral accepted successfully."
                      : "Referral declined successfully."}
                  </h1>

                  <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                    The relevant Zaviramed team, doctor, and patient can now be
                    notified based on your decision workflow.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 text-sm text-slate-300">
                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Patient</span>
                  <span className="max-w-[180px] truncate font-medium text-white">
                    {patientName}
                  </span>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Decision</span>
                  <span
                    className={`font-medium ${
                      accepted ? "text-emerald-300" : "text-red-300"
                    }`}
                  >
                    {accepted ? "Accepted" : "Declined"}
                  </span>
                </div>

                <div className="flex items-center justify-between border-t border-white/10 pt-5">
                  <span>Referral ID</span>
                  <span className="max-w-[180px] truncate font-medium text-white">
                    {id}
                  </span>
                </div>
              </div>
            </aside>

            <section className="p-5 sm:p-8 md:p-10 lg:p-12">
              <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
                <img
                  src="/logo_new_nobg.png"
                  alt="Zaviramed"
                  className="h-10 w-auto object-contain"
                />

                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                  <ShieldCheck size={14} />
                  Secure
                </div>
              </div>

              <div
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${
                  accepted
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-red-200 bg-red-50 text-red-700"
                }`}
              >
                {accepted ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                {accepted ? "Referral accepted" : "Referral declined"}
              </div>

              <div className="mt-8 max-w-2xl">
                <div
                  className={`mb-7 flex h-16 w-16 items-center justify-center rounded-2xl border ${
                    accepted
                      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : "border-red-200 bg-red-50 text-red-700"
                  }`}
                >
                  {accepted ? <CheckCircle2 size={32} /> : <XCircle size={32} />}
                </div>

                <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                  {accepted
                    ? "You accepted this referral"
                    : "You declined this referral"}
                </h2>

                <p className="mt-4 text-base leading-7 text-slate-600">
                  The decision has been submitted. The doctor and patient will
                  be notified according to the referral workflow.
                </p>

                <div className="mt-8 rounded-2xl border border-slate-200">
                  <div className="border-b border-slate-200 px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Referral summary
                    </p>
                  </div>

                  <div className="grid gap-0 sm:grid-cols-2">
                    <div className="border-b border-slate-200 px-4 py-4 sm:border-b-0 sm:border-r">
                      <p className="text-xs font-medium text-slate-500">
                        Patient
                      </p>
                      <p className="mt-1 truncate text-sm font-semibold text-slate-950">
                        {patientName}
                      </p>
                    </div>

                    <div className="px-4 py-4">
                      <p className="text-xs font-medium text-slate-500">
                        Decision
                      </p>
                      <p
                        className={`mt-1 text-sm font-semibold ${
                          accepted ? "text-emerald-700" : "text-red-700"
                        }`}
                      >
                        {accepted ? "Accepted" : "Declined"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F8FAFC] text-slate-950">
      <section className="mx-auto flex min-h-screen w-full max-w-6xl items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-slate-200 bg-white lg:grid-cols-[0.95fr_1.05fr]">
          <aside className="hidden border-r border-slate-200 bg-slate-950 p-10 text-white lg:flex lg:flex-col lg:justify-between">
            <div>
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-12 w-auto object-contain"
              />

              <div className="mt-16">
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                  <ShieldCheck size={15} />
                  Secure referral decision
                </div>

                <h1 className="max-w-md text-4xl font-semibold tracking-[-0.04em] text-white">
                  Review the referral before accepting.
                </h1>

                <p className="mt-5 max-w-sm text-sm leading-7 text-slate-300">
                  Confirm that your facility or doctor can receive this patient
                  before accepting the referral.
                </p>
              </div>
            </div>

            <div className="grid gap-3 text-sm text-slate-300">
              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Patient</span>
                <span className="max-w-[180px] truncate font-medium text-white">
                  {patientName}
                </span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Referral ID</span>
                <span className="max-w-[180px] truncate font-medium text-white">
                  {id}
                </span>
              </div>

              <div className="flex items-center justify-between border-t border-white/10 pt-5">
                <span>Status</span>
                <span className="font-medium text-white">
                  {referralStatusLabel}
                </span>
              </div>
            </div>
          </aside>

          <section className="p-5 sm:p-8 md:p-10 lg:p-12">
            <div className="mb-10 flex items-center justify-between gap-4 lg:hidden">
              <img
                src="/logo_new_nobg.png"
                alt="Zaviramed"
                className="h-10 w-auto object-contain"
              />

              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                <ShieldCheck size={14} />
                Secure
              </div>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
              <ClipboardList size={14} />
              Referral review
            </div>

            <div className="mt-8 max-w-2xl">
              <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700">
                <Stethoscope size={32} />
              </div>

              <h2 className="text-3xl font-semibold tracking-[-0.04em] text-slate-950 sm:text-4xl">
                Patient referral request
              </h2>

              <p className="mt-4 text-base leading-7 text-slate-600">
                Review the patient information below, then accept or decline the
                referral. Once submitted, the doctor and patient will be notified.
              </p>

              {error && (
                <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4">
                  <p className="text-sm leading-6 text-red-700">{error}</p>
                </div>
              )}
            </div>

            <div className="mt-8 rounded-2xl border border-slate-200">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Patient details
                </p>
              </div>

              <div className="grid gap-0 sm:grid-cols-2">
                <div className="border-b border-slate-200 px-4 py-4 sm:border-r">
                  <p className="text-xs font-medium text-slate-500">Patient</p>
                  <p className="mt-1 text-sm font-semibold text-slate-950">
                    {patientName}
                  </p>
                </div>

                <div className="border-b border-slate-200 px-4 py-4">
                  <p className="text-xs font-medium text-slate-500">
                    Referral ID
                  </p>
                  <p className="mt-1 truncate text-sm font-semibold text-slate-950">
                    {id}
                  </p>
                </div>

                {patientPhone && (
                  <div className="border-b border-slate-200 px-4 py-4 sm:border-r">
                    <p className="text-xs font-medium text-slate-500">Phone</p>
                    <p className="mt-1 text-sm font-semibold text-slate-950">
                      {patientPhone}
                    </p>
                  </div>
                )}

                {patientEmail && (
                  <div className="border-b border-slate-200 px-4 py-4">
                    <p className="text-xs font-medium text-slate-500">Email</p>
                    <p className="mt-1 truncate text-sm font-semibold text-slate-950">
                      {patientEmail}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Clinical summary
                </p>
              </div>

              <div className="divide-y divide-slate-200">
                <div className="px-4 py-4">
                  <p className="text-xs font-medium text-slate-500">
                    Diagnosis
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-800">
                    {referral.diagnosis || "Not provided"}
                  </p>
                </div>

                <div className="px-4 py-4">
                  <p className="text-xs font-medium text-slate-500">
                    Clinical notes
                  </p>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-800">
                    {referral.clinicalNotes || "Not provided"}
                  </p>
                </div>

                <div className="px-4 py-4">
                  <p className="text-xs font-medium text-slate-500">
                    Medications
                  </p>

                  {referral.medications?.length > 0 ? (
                    <ul className="mt-3 grid gap-2">
                      {referral.medications.map((medication, index) => (
                        <li
                          key={`${medication}-${index}`}
                          className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-800"
                        >
                          {medication}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">
                      No medications listed.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => handleDecision("decline")}
                disabled={decisionLoading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-50 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {decisionLoading && decisionType === "decline" ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <XCircle size={18} />
                )}
                Decline referral
              </button>

              <button
                type="button"
                onClick={() => handleDecision("accept")}
                disabled={decisionLoading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0F8F5F] px-5 py-4 text-sm font-semibold text-white transition hover:bg-[#0B7A50] active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-slate-300 sm:w-auto"
              >
                {decisionLoading && decisionType === "accept" ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={18} />
                )}
                Accept referral
              </button>
            </div>

            <div className="mt-10 grid gap-3 border-t border-slate-200 pt-6 text-sm text-slate-600">
              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  Accept only if the receiving doctor, facility, or service can
                  attend to this patient.
                </p>
              </div>

              <div className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400" />
                <p>
                  If the patient requires urgent care, ensure they are directed
                  to the nearest appropriate emergency facility.
                </p>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};

export default ReferralDecisionPage;